import React, { useState, useEffect, useCallback, useContext } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  IconButton,
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  CircularProgress,
  Typography,
  Switch,
  FormControlLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useSnackbar } from 'notistack';
import { auth, db, functions } from '../../firebase';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where,
  getDoc 
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { setDoc } from 'firebase/firestore';
import { addLog } from '../../services/logs';
import { AuthContext } from '../../contexts/AuthContext';

const INITIAL_FORM_STATE = {
  username: '',
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  role: 'employee',
  defaultLocation: 'PAN',
  isActive: true
};

// Lista lokalizacji zgodna z tymi używanymi przy dodawaniu produktów
const LOCATIONS = [
  { value: 'PAN', label: 'Panorama M&M GOLD' },
  { value: 'VVS', label: 'Panorama VVS' },
  { value: 'MODO', label: 'Modo M&M GOLD' },
  { value: 'KLIF', label: 'Klif M&M GOLD' },
  { value: 'GP', label: 'Galeria Północna M&M GOLD' },
  { value: 'SB', label: 'Stary Browar M&M GOLD' },
  { value: 'MB', label: 'Marbella' },
  { value: 'REMOTE', label: 'Zdalnie' }
];

// Role użytkowników
const USER_ROLES = [
  { value: 'employee', label: 'Pracownik' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'admin', label: 'Administrator' }
];


const UserManagement = () => {
  const { userRole } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'employee',
    defaultLocation: 'PAN',
    isActive: true
  });

  const fetchUsers = useCallback(async () => {
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      enqueueSnackbar('Błąd podczas pobierania użytkowników', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (userRole === 'admin') {
      fetchUsers();
    }
  }, [fetchUsers, userRole]);

  // Jeśli użytkownik nie jest adminem, wyświetl komunikat o braku dostępu
  if (userRole !== 'admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="error" sx={{ fontSize: '1.25rem' }}>
          Brak dostępu. Ta strona jest dostępna tylko dla administratorów.
        </Typography>
      </Box>
    );
  }
  const handleOpen = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        username: user.username || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        password: '',
        role: user.role || 'employee',
        defaultLocation: user.defaultLocation || 'PAN',
        isActive: user.hasOwnProperty('isActive') ? user.isActive : true
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'employee',
        defaultLocation: 'PAN',
        isActive: true
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUser(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingUser) {
        // Aktualizacja istniejącego użytkownika
        const userRef = doc(db, 'users', editingUser.id);
        const userData = {
          username: formData.username,
          firstName: formData.firstName,
          lastName: formData.lastName,
          displayName: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          role: formData.role,
          defaultLocation: formData.defaultLocation,
          isActive: formData.isActive,
          updatedAt: new Date().toISOString()
        };
        await updateDoc(userRef, userData);

        // Dodaj log o aktualizacji użytkownika
        await addLog({
          action: 'user_update',
          details: {
            username: formData.username,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            role: formData.role,
            defaultLocation: formData.defaultLocation
          },
          entity_type: 'user',
          entity_id: editingUser.id,
          user_id: auth.currentUser.uid,
          location: formData.defaultLocation
        });
        enqueueSnackbar('Użytkownik został zaktualizowany', { variant: 'success' });
      } else {
        // Sprawdź, czy jesteśmy zalogowani jako admin
        if (!auth.currentUser || !auth.currentUser.uid) {
          throw new Error('Brak zalogowanego administratora');
        }

        // Przygotowujemy dane użytkownika
        const userData = {
          username: formData.username,
          firstName: formData.firstName,
          lastName: formData.lastName,
          displayName: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          role: formData.role,
          defaultLocation: formData.defaultLocation,
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        // Zmienna, która będzie dostępna po obu ścieżkach (Cloud Function i direct)
        let createdUserUid;
        // Zapisujemy aktualnego administratora, aby móc się ponownie zalogować po stworzeniu użytkownika
        const currentUser = auth.currentUser;
        try {
          console.log('Using Cloud Function to create user:', formData.email);
          
          // Wywołujemy Cloud Function do tworzenia użytkowników
          const createUserFunction = httpsCallable(functions, 'createUser');
          const result = await createUserFunction({
            email: formData.email,
            password: formData.password,
            userData: userData
          });
          
          createdUserUid = result.data.uid;
          console.log('User successfully created with UID:', createdUserUid);
        } catch (error) {
          console.error('Error with Cloud Function:', error);
          
          // Jeśli Cloud Function zawiedzie, próbujemy alternatywnej metody
          console.log('Falling back to direct method for creating user:', formData.email);
          
          try {
            // Tworzymy użytkownika w Firebase Authentication
            const authResult = await createUserWithEmailAndPassword(
              auth,
              formData.email,
              formData.password
            );
            
            createdUserUid = authResult.user.uid;
            console.log('User created in Auth with UID:', createdUserUid);

            // Wylogowujemy nowego użytkownika
            await signOut(auth);
            
            // Ponownie logujemy administratora
            await signInWithEmailAndPassword(auth, currentUser.email, currentUser.password || 'Admin123!');
            
            // Tworzymy dokument użytkownika w Firestore
            console.log('Creating user document in Firestore with UID:', createdUserUid);
            await setDoc(doc(db, 'users', createdUserUid), userData);
            console.log('User document created successfully');
          } catch (directError) {
            console.error('Error with direct method:', directError);
            throw directError;
          }
        }

        // Dodaj log o utworzeniu użytkownika
        await addLog({
          action: 'user_create',
          details: {
            username: formData.username,
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            role: formData.role,
            defaultLocation: formData.defaultLocation
          },
          entity_type: 'user',
          entity_id: createdUserUid,
          user_id: auth.currentUser.uid,
          location: formData.defaultLocation
        });

        // Odśwież dane użytkownika
        await auth.currentUser.reload();
        
        // Odśwież listę użytkowników
        await fetchUsers();
        
        enqueueSnackbar('Użytkownik został utworzony', { variant: 'success' });
      }

      handleClose();
    } catch (error) {
      console.error('Error:', error);
      enqueueSnackbar(
        error.code === 'auth/email-already-in-use'
          ? 'Ten email jest już zajęty'
          : 'Wystąpił błąd podczas zapisywania użytkownika',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Czy na pewno chcesz usunąć tego użytkownika?')) {
      try {
        // Pobierz dane użytkownika przed usunięciem
        const userRef = doc(db, 'users', userId);
        const userSnap = await getDoc(userRef);
        const userData = userSnap.data();

        // Usuń użytkownika z Firestore
        await deleteDoc(userRef);

        // Dodaj log o usunięciu użytkownika
        await addLog({
          action: 'user_delete',
          details: {
            username: userData.username,
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            role: userData.role,
            defaultLocation: userData.defaultLocation
          },
          entity_type: 'user',
          entity_id: userId,
          user_id: auth.currentUser.uid,
          location: userData.defaultLocation
        });

        await fetchUsers();
        enqueueSnackbar('Użytkownik został usunięty', { variant: 'success' });
      } catch (error) {
        console.error('Error deleting user:', error);
        enqueueSnackbar('Błąd podczas usuwania użytkownika', { variant: 'error' });
      }
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3
      }}>
        <Button
          color="primary"
          onClick={() => handleOpen()}
          sx={{
            borderRadius: 2,
            fontFamily: 'Montserrat',
            textTransform: 'none'
          }}
        >
          Dodaj Użytkownika
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: 'Montserrat', fontWeight: 600 }}>Nazwa użytkownika</TableCell>
                <TableCell sx={{ fontFamily: 'Montserrat', fontWeight: 600 }}>Imię</TableCell>
                <TableCell sx={{ fontFamily: 'Montserrat', fontWeight: 600 }}>Nazwisko</TableCell>
                <TableCell sx={{ fontFamily: 'Montserrat', fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontFamily: 'Montserrat', fontWeight: 600 }}>Rola</TableCell>
                <TableCell sx={{ fontFamily: 'Montserrat', fontWeight: 600 }}>Lokalizacja</TableCell>
                <TableCell sx={{ fontFamily: 'Montserrat', fontWeight: 600 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Montserrat', fontWeight: 600 }}>Akcje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell sx={{ fontFamily: 'Montserrat' }}>{user.username}</TableCell>
                  <TableCell sx={{ fontFamily: 'Montserrat' }}>{user.firstName}</TableCell>
                  <TableCell sx={{ fontFamily: 'Montserrat' }}>{user.lastName}</TableCell>
                  <TableCell sx={{ fontFamily: 'Montserrat' }}>{user.email}</TableCell>
                  <TableCell sx={{ fontFamily: 'Montserrat' }}>
                    {user.role === 'admin' ? 'Administrator' : 
                     user.role === 'marketing' ? 'Marketing' : 'Pracownik'}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Montserrat' }}>
                    {LOCATIONS.find(loc => loc.value === user.defaultLocation)?.label || user.defaultLocation}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'Montserrat' }}>
                    {user.isActive ? 
                     <Typography color="success.main">Aktywny</Typography> : 
                     <Typography color="error.main">Nieaktywny</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      onClick={() => handleOpen(user)}
                      size="small"
                      sx={{ 
                        mr: 1,
                        color: 'primary.main',
                        '&:hover': { 
                          backgroundColor: 'rgba(212, 175, 55, 0.1)' 
                        }
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDelete(user.id)}
                      size="small"
                      sx={{ 
                        color: 'error.main',
                        '&:hover': { 
                          backgroundColor: 'rgba(211, 47, 47, 0.1)' 
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 2,
            backgroundColor: 'background.paper'
          }
        }}
      >
        <DialogTitle sx={{
          fontFamily: 'Montserrat',
          color: 'primary.main'
        }}>
          {editingUser ? 'Edytuj Użytkownika' : 'Dodaj Użytkownika'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              autoFocus
              margin="normal"
              name="username"
              label="Nazwa użytkownika"
              type="text"
              fullWidth
              value={formData.username}
              onChange={handleChange}
              required
              variant="outlined"
              sx={{ fontFamily: 'Montserrat' }}
              helperText="Unikalna nazwa użytkownika"
            />
            <TextField
              margin="normal"
              name="firstName"
              label="Imię"
              type="text"
              fullWidth
              value={formData.firstName}
              onChange={handleChange}
              required
              variant="outlined"
              sx={{ fontFamily: 'Montserrat' }}
            />
            <TextField
              margin="normal"
              name="lastName"
              label="Nazwisko"
              type="text"
              fullWidth
              value={formData.lastName}
              onChange={handleChange}
              required
              variant="outlined"
              sx={{ fontFamily: 'Montserrat' }}
            />
            <TextField
              margin="normal"
              name="email"
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={handleChange}
              required
              variant="outlined"
              sx={{ fontFamily: 'Montserrat' }}
              helperText="Będzie używany do logowania"
            />
            <TextField
              margin="normal"
              name="password"
              label="Hasło"
              type="password"
              fullWidth
              value={formData.password}
              onChange={handleChange}
              required={!editingUser}
              variant="outlined"
              sx={{ fontFamily: 'Montserrat' }}
              helperText={!editingUser ? 'Minimum 6 znaków' : 'Zostaw puste jeśli nie chcesz zmieniać'}
            />
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel sx={{ fontFamily: 'Montserrat' }}>Rola</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleChange}
                required
                label="Rola"
                sx={{ fontFamily: 'Montserrat' }}
              >
                {USER_ROLES.map((role) => (
                  <MenuItem 
                    key={role.value} 
                    value={role.value} 
                    sx={{ fontFamily: 'Montserrat' }}
                  >
                    {role.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" variant="outlined">
              <InputLabel sx={{ fontFamily: 'Montserrat' }}>Domyślna lokalizacja</InputLabel>
              <Select
                name="defaultLocation"
                value={formData.defaultLocation}
                onChange={handleChange}
                required
                label="Domyślna lokalizacja"
                sx={{ fontFamily: 'Montserrat' }}
              >
                {LOCATIONS.map((location) => (
                  <MenuItem 
                    key={location.value} 
                    value={location.value} 
                    sx={{ fontFamily: 'Montserrat' }}
                  >
                    {location.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {/* Przycisk aktywności użytkownika (tylko dla administratora) */}
            {userRole === 'admin' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                    name="isActive"
                    color="primary"
                  />
                }
                label={(
                  <Typography sx={{ fontFamily: 'Montserrat' }}>
                    {formData.isActive ? 'Użytkownik aktywny' : 'Użytkownik nieaktywny'}
                  </Typography>
                )}
                sx={{ mt: 2 }}
              />
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button 
              onClick={handleClose}
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                fontFamily: 'Montserrat'
              }}
            >
              Anuluj
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              sx={{ 
                borderRadius: 2,
                fontFamily: 'Montserrat'
              }}
            >
              {editingUser ? 'Zapisz' : 'Dodaj'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default UserManagement;
